/**
 * PartyBroadcastService - Apply effects to all party members simultaneously
 *
 * Used by cards like:
 * - Healing Field: All Close allies clear 1-2 HP
 * - Battle Cry: All allies clear Stress, gain Hope, advantage until fail
 * - Rousing Strike: All allies clear HP or Stress on crit
 * - Critical Inspiration: All allies clear Stress OR gain Hope
 * - Soothing Speech: Party rest benefits
 *
 * Effect types:
 * - clearHP: Heal HP (clear marked HP slots)
 * - clearStress: Clear Stress marks
 * - grantHope: Add Hope
 * - grantAdvantage: Grant advantage on next roll
 *
 * Usage:
 *   await broadcastEffect(campaignId, partyMembers, {
 *     type: 'clearHP',
 *     amount: 1,
 *     source: 'Healing Field',
 *     sourceCharacterId: 'char_123',
 *     filter: (member) => member.hp < member.maxHp
 *   });
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, rtdb } from '../firebase/config';
import { ref, get } from 'firebase/database';
import toast from 'react-hot-toast';
import { grantAllyModifier, createAdvantageModifier } from './AllyModifierService';

/**
 * Get all party members from RTDB
 *
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Array>} Array of party member vitals
 */
export const getPartyMembers = async (campaignId) => {
  if (!campaignId) return [];

  const vitalsRef = ref(rtdb, `campaigns/${campaignId}/partyVitals`);
  const snapshot = await get(vitalsRef);

  if (!snapshot.exists()) return [];

  const vitalsData = snapshot.val();
  return Object.entries(vitalsData).map(([characterId, data]) => ({
    characterId,
    ...data
  }));
};

/**
 * Apply an effect to a single character in Firestore
 *
 * @param {string} characterId - Character document ID
 * @param {Object} effect - Effect to apply
 * @returns {Promise<Object>} Result with success status and message
 */
const applyEffectToCharacter = async (characterId, effect) => {
  try {
    const charRef = doc(db, 'characters', characterId);
    const charDoc = await getDoc(charRef);

    if (!charDoc.exists()) {
      return { success: false, message: `Character ${characterId} not found` };
    }

    const charData = charDoc.data();
    const updates = {};
    let message = '';

    switch (effect.type) {
      case 'clearHP': {
        const currentHP = charData.hp || 0;
        const maxHP = charData.maxHp || 8;
        const healAmount = Math.min(effect.amount || 1, maxHP - currentHP);

        if (healAmount > 0) {
          updates.hp = currentHP + healAmount;
          message = `Healed ${healAmount} HP`;
        } else {
          message = 'HP already full';
        }
        break;
      }

      case 'clearStress': {
        const currentStress = charData.stress || 0;
        const clearAmount = Math.min(effect.amount || 1, currentStress);

        if (clearAmount > 0) {
          updates.stress = currentStress - clearAmount;
          message = `Cleared ${clearAmount} Stress`;
        } else {
          message = 'No Stress to clear';
        }
        break;
      }

      case 'grantHope': {
        const currentHope = charData.hope || 0;
        const maxHope = 6 - (charData.scars?.length || 0);
        const hopeToAdd = Math.min(effect.amount || 1, maxHope - currentHope);

        if (hopeToAdd > 0) {
          updates.hope = currentHope + hopeToAdd;
          message = `Gained ${hopeToAdd} Hope`;
        } else {
          message = 'Hope already full';
        }
        break;
      }

      case 'grantAdvantage': {
        // Use AllyModifierService to grant advantage
        // This is handled separately below
        message = 'Granted advantage';
        break;
      }

      default:
        return { success: false, message: `Unknown effect type: ${effect.type}` };
    }

    // Apply Firestore updates if any
    if (Object.keys(updates).length > 0) {
      await updateDoc(charRef, updates);
    }

    return { success: true, message, characterId, updates };
  } catch (error) {
    console.error(`[PartyBroadcast] Error applying effect to ${characterId}:`, error);
    return { success: false, message: error.message, characterId };
  }
};

/**
 * Broadcast an effect to all party members
 *
 * @param {string} campaignId - Campaign ID for RTDB lookup
 * @param {Object} effect - Effect configuration
 * @param {string} effect.type - Effect type (clearHP, clearStress, grantHope, grantAdvantage)
 * @param {number} effect.amount - Amount for the effect
 * @param {string} effect.source - Name of the ability/card
 * @param {string} effect.sourceCharacterId - Character ID of the source
 * @param {string} effect.sourceName - Display name of the source character
 * @param {Function} [effect.filter] - Optional filter function (member) => boolean
 * @param {boolean} [effect.excludeSelf] - If true, exclude source character (default: false)
 * @returns {Promise<Object>} Results summary
 */
export const broadcastEffect = async (campaignId, effect) => {
  if (!campaignId) {
    return { success: false, message: 'Campaign ID required' };
  }

  // Get party members from RTDB
  const partyMembers = await getPartyMembers(campaignId);

  if (partyMembers.length === 0) {
    return { success: false, message: 'No party members found' };
  }

  // Filter members
  let targets = partyMembers;

  // Exclude self if requested
  if (effect.excludeSelf && effect.sourceCharacterId) {
    targets = targets.filter(m => m.characterId !== effect.sourceCharacterId);
  }

  // Apply custom filter
  if (effect.filter && typeof effect.filter === 'function') {
    targets = targets.filter(effect.filter);
  }

  if (targets.length === 0) {
    return { success: true, message: 'No valid targets', affected: 0 };
  }

  // Apply effect to each target
  const results = [];

  for (const target of targets) {
    if (effect.type === 'grantAdvantage') {
      // Use AllyModifierService for advantage
      try {
        await grantAllyModifier(campaignId, target.characterId, createAdvantageModifier(
          { characterId: effect.sourceCharacterId, name: effect.sourceName },
          effect.source,
          effect.expiresOn || 'nextRoll'
        ));
        results.push({ success: true, characterId: target.characterId, message: 'Granted advantage' });
      } catch (error) {
        results.push({ success: false, characterId: target.characterId, message: error.message });
      }
    } else {
      // Use Firestore update for vitals changes
      const result = await applyEffectToCharacter(target.characterId, effect);
      results.push(result);
    }
  }

  // Build summary
  const successful = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);

  return {
    success: failed.length === 0,
    affected: successful.length,
    failed: failed.length,
    results,
    message: `Applied ${effect.source} to ${successful.length} party member(s)`
  };
};

/**
 * Broadcast healing to all party members
 *
 * @param {string} campaignId - Campaign ID
 * @param {number} amount - HP to heal
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @param {boolean} excludeSelf - Exclude the source character
 * @returns {Promise<Object>} Results
 */
export const broadcastHealing = async (campaignId, amount, source, excludeSelf = true) => {
  const result = await broadcastEffect(campaignId, {
    type: 'clearHP',
    amount,
    source: source.abilityName,
    sourceCharacterId: source.characterId,
    sourceName: source.name,
    excludeSelf,
    filter: (member) => member.hp < member.maxHp // Only target damaged members
  });

  if (result.affected > 0) {
    toast.success(`${source.abilityName}: Healed ${result.affected} allies!`, {
      icon: '💚',
      duration: 3000
    });
  }

  return result;
};

/**
 * Broadcast stress clearing to all party members
 *
 * @param {string} campaignId - Campaign ID
 * @param {number} amount - Stress to clear
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @param {boolean} excludeSelf - Exclude the source character
 * @returns {Promise<Object>} Results
 */
export const broadcastStressClearing = async (campaignId, amount, source, excludeSelf = true) => {
  const result = await broadcastEffect(campaignId, {
    type: 'clearStress',
    amount,
    source: source.abilityName,
    sourceCharacterId: source.characterId,
    sourceName: source.name,
    excludeSelf,
    filter: (member) => member.stress > 0 // Only target stressed members
  });

  if (result.affected > 0) {
    toast.success(`${source.abilityName}: Cleared Stress for ${result.affected} allies!`, {
      icon: '💙',
      duration: 3000
    });
  }

  return result;
};

/**
 * Broadcast Hope granting to all party members
 *
 * @param {string} campaignId - Campaign ID
 * @param {number} amount - Hope to grant
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @param {boolean} excludeSelf - Exclude the source character
 * @returns {Promise<Object>} Results
 */
export const broadcastHope = async (campaignId, amount, source, excludeSelf = true) => {
  const result = await broadcastEffect(campaignId, {
    type: 'grantHope',
    amount,
    source: source.abilityName,
    sourceCharacterId: source.characterId,
    sourceName: source.name,
    excludeSelf,
    filter: (member) => member.hope < member.maxHope // Only target members who can gain Hope
  });

  if (result.affected > 0) {
    toast.success(`${source.abilityName}: Granted Hope to ${result.affected} allies!`, {
      icon: '✨',
      duration: 3000
    });
  }

  return result;
};

/**
 * Broadcast advantage to all party members
 *
 * @param {string} campaignId - Campaign ID
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @param {string} expiresOn - When advantage expires
 * @param {boolean} excludeSelf - Exclude the source character
 * @returns {Promise<Object>} Results
 */
export const broadcastAdvantage = async (campaignId, source, expiresOn = 'nextRoll', excludeSelf = true) => {
  const result = await broadcastEffect(campaignId, {
    type: 'grantAdvantage',
    source: source.abilityName,
    sourceCharacterId: source.characterId,
    sourceName: source.name,
    excludeSelf,
    expiresOn
  });

  if (result.affected > 0) {
    toast.success(`${source.abilityName}: Granted advantage to ${result.affected} allies!`, {
      icon: '🎯',
      duration: 3000
    });
  }

  return result;
};

/**
 * Battle Cry effect - combines multiple effects
 * All allies: clear Stress, gain Hope, advantage until fail
 *
 * @param {string} campaignId - Campaign ID
 * @param {Object} source - Source info { characterId, name }
 * @returns {Promise<Object>} Combined results
 */
export const battleCryEffect = async (campaignId, source) => {
  const abilityName = 'Battle Cry';
  const sourceInfo = { ...source, abilityName };

  // Apply all three effects
  const stressResult = await broadcastStressClearing(campaignId, 1, sourceInfo, true);
  const hopeResult = await broadcastHope(campaignId, 1, sourceInfo, true);
  const advantageResult = await broadcastAdvantage(campaignId, sourceInfo, 'nextFailure', true);

  // Single combined toast
  toast.success(`Battle Cry activated! Party empowered!`, {
    icon: '⚔️',
    duration: 4000
  });

  return {
    success: true,
    stressResult,
    hopeResult,
    advantageResult
  };
};

/**
 * Apply healing to a specific ally (targeted healing)
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} targetCharacterId - Target character's document ID
 * @param {number} amount - HP to heal
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @returns {Promise<Object>} Result with success status
 */
export const broadcastTargetedHealing = async (campaignId, targetCharacterId, amount, source) => {
  if (!campaignId || !targetCharacterId) {
    return { success: false, message: 'Campaign ID and target character ID required' };
  }

  const result = await applyEffectToCharacter(targetCharacterId, {
    type: 'clearHP',
    amount,
    source: source.abilityName,
    sourceCharacterId: source.characterId,
    sourceName: source.name
  });

  return result;
};

/**
 * Clear stress from a specific ally (targeted stress clearing)
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} targetCharacterId - Target character's document ID
 * @param {number} amount - Stress to clear
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @returns {Promise<Object>} Result with success status
 */
export const broadcastTargetedStressClear = async (campaignId, targetCharacterId, amount, source) => {
  if (!campaignId || !targetCharacterId) {
    return { success: false, message: 'Campaign ID and target character ID required' };
  }

  const result = await applyEffectToCharacter(targetCharacterId, {
    type: 'clearStress',
    amount,
    source: source.abilityName,
    sourceCharacterId: source.characterId,
    sourceName: source.name
  });

  return result;
};

/**
 * Grant Hope to a specific ally (targeted Hope granting)
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} targetCharacterId - Target character's document ID
 * @param {number} amount - Hope to grant
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @returns {Promise<Object>} Result with success status
 */
export const broadcastTargetedHope = async (campaignId, targetCharacterId, amount, source) => {
  if (!campaignId || !targetCharacterId) {
    return { success: false, message: 'Campaign ID and target character ID required' };
  }

  const result = await applyEffectToCharacter(targetCharacterId, {
    type: 'grantHope',
    amount,
    source: source.abilityName,
    sourceCharacterId: source.characterId,
    sourceName: source.name
  });

  return result;
};

/**
 * Grant temporary armor to a specific ally
 * Used by: Tava's Armor
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} targetCharacterId - Target character's document ID
 * @param {Object} armorBuff - Armor buff configuration { amount, expiresOn }
 * @param {Object} source - Source info { characterId, name, abilityName }
 * @returns {Promise<Object>} Result with success status
 */
export const broadcastTargetedArmorBuff = async (campaignId, targetCharacterId, armorBuff, source) => {
  if (!targetCharacterId) {
    return { success: false, message: 'Target character ID required' };
  }

  try {
    // First, clear any existing armor buff from this caster on ANY party member
    if (campaignId && source.characterId) {
      const partyMembers = await getPartyMembers(campaignId);
      for (const member of partyMembers) {
        // Check if this member has a buff from our caster
        const memberRef = doc(db, 'characters', member.characterId);
        const memberDoc = await getDoc(memberRef);
        if (memberDoc.exists()) {
          const memberData = memberDoc.data();
          if (memberData.temporaryArmor?.grantedBy === source.characterId) {
            // Clear the previous buff
            await updateDoc(memberRef, { temporaryArmor: null });
          }
        }
      }
    }

    // Apply temporary armor to target
    const targetRef = doc(db, 'characters', targetCharacterId);
    const targetDoc = await getDoc(targetRef);

    if (!targetDoc.exists()) {
      return { success: false, message: 'Target character not found' };
    }

    await updateDoc(targetRef, {
      temporaryArmor: {
        amount: armorBuff.amount || 1,
        source: armorBuff.source || 'tavas-armor',
        grantedBy: source.characterId,
        grantedByName: source.name,
        expiresOn: armorBuff.expiresOn || 'rest',
        grantedAt: Date.now()
      }
    });

    return {
      success: true,
      message: `Granted +${armorBuff.amount || 1} temporary armor`,
      targetCharacterId
    };
  } catch (error) {
    console.error('[PartyBroadcast] Error applying armor buff:', error);
    return { success: false, message: error.message };
  }
};

export default {
  getPartyMembers,
  broadcastEffect,
  broadcastHealing,
  broadcastStressClearing,
  broadcastHope,
  broadcastAdvantage,
  battleCryEffect,
  broadcastTargetedHealing,
  broadcastTargetedStressClear,
  broadcastTargetedHope,
  broadcastTargetedArmorBuff
};
